import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError, download } from "../lib/api";
import { useActiveCategories, useMeta } from "../lib/meta";
import type { Card, EarnResult, Transaction } from "../lib/types";
import { formatINR, formatDate, formatPoints, todayInput, toDateInput } from "../lib/format";
import { Badge, BlockedAction, EmptyState, ErrorBanner, Modal, PageHeader, Panel, Spinner } from "../components/ui";

interface ListResponse {
  transactions: Transaction[];
  total: number;
  totalAmount: number;
  totalPoints: number;
}

const BLANK = {
  cardId: "",
  date: todayInput(),
  amount: "",
  merchant: "",
  bookedVia: "",
  category: "DINING",
  channel: "OFFLINE" as "ONLINE" | "OFFLINE",
  isInternational: false,
  notes: "",
  countsForMilestone: true,
  pointsOverride: "",
};

export default function TransactionsPage() {
  const meta = useMeta();
  const categories = useActiveCategories();
  const [cards, setCards] = useState<Card[]>([]);
  const [data, setData] = useState<ListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [filterCard, setFilterCard] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const [formOpen, setFormOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState<Transaction | null>(null);
  const [form, setForm] = useState({ ...BLANK });
  const [preview, setPreview] = useState<EarnResult | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (filterCard) params.set("cardId", filterCard);
    if (filterCategory) params.set("category", filterCategory);
    if (search.trim()) params.set("search", search.trim());
    params.set("limit", String(pageSize));
    params.set("offset", String(page * pageSize));
    try {
      const r = await api.get<ListResponse>(`/transactions?${params}`);
      setData(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load transactions");
    }
  }, [filterCard, filterCategory, search, page, pageSize]);

  useEffect(() => {
    api.get<{ cards: Card[] }>("/cards").then((r) => setCards(r.cards)).catch(() => undefined);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => void load(), search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const activeCards = useMemo(() => cards.filter((c) => c.isActive), [cards]);

  useEffect(() => { setPage(0); }, [filterCard, filterCategory, search, pageSize]);

  // Ask the server what this spend would earn, so the number shown while
  // typing is the same engine that will score the saved transaction.
  useEffect(() => {
    if (!formOpen || !form.cardId || !Number(form.amount)) {
      setPreview(null);
      return;
    }
    const timer = setTimeout(() => {
      api
        .post<{ earn: EarnResult }>("/transactions/preview", {
          cardId: form.cardId,
          date: form.date,
          amount: Number(form.amount),
          merchant: form.merchant || "—",
          bookedVia: form.bookedVia.trim() || null,
          category: form.category,
          channel: form.channel,
          isInternational: form.isInternational,
        })
        .then((r) => setPreview(r.earn))
        .catch(() => setPreview(null));
    }, 250);
    return () => clearTimeout(timer);
  }, [formOpen, form.cardId, form.amount, form.merchant, form.bookedVia, form.category, form.channel, form.isInternational, form.date]);

  function openNew() {
    setEditing(null);
    setForm({ ...BLANK, date: todayInput(), cardId: activeCards[0]?.id ?? "", category: categories[0]?.key ?? "OTHER" });
    setPreview(null);
    setFormOpen(true);
  }

  function openEdit(t: Transaction) {
    setEditing(t);
    setForm({
      cardId: t.cardId,
      date: toDateInput(t.date),
      amount: String(t.amount),
      merchant: t.merchant,
      bookedVia: t.bookedVia ?? "",
      category: t.category,
      channel: t.channel as "ONLINE" | "OFFLINE",
      isInternational: t.isInternational,
      notes: t.notes ?? "",
      countsForMilestone: t.countsForMilestone,
      pointsOverride: t.pointsOverride != null ? String(t.pointsOverride) : "",
    });
    setFormOpen(true);
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const payload = {
      cardId: form.cardId,
      date: form.date,
      amount: Number(form.amount),
      merchant: form.merchant.trim(),
      bookedVia: form.bookedVia.trim() || null,
      category: form.category,
      channel: form.channel,
      isInternational: form.isInternational,
      notes: form.notes.trim() || null,
      countsForMilestone: form.countsForMilestone,
      // Empty clears the override and hands the row back to the engine.
      pointsOverride: form.pointsOverride.trim() === "" ? null : Number(form.pointsOverride),
    };
    try {
      if (editing) await api.put(`/transactions/${editing.id}`, payload);
      else await api.post("/transactions", payload);
      setFormOpen(false);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the transaction");
    } finally {
      setSaving(false);
    }
  }

  async function remove(t: Transaction) {
    if (!confirm(`Delete the ${formatINR(t.amount)} spend at ${t.merchant}?`)) return;
    try {
      await api.del(`/transactions/${t.id}`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not delete the transaction");
    }
  }

  async function recalculate() {
    setNotice(null);
    try {
      const r = await api.post<{ updated: number }>("/transactions/recalculate", {});
      setNotice(`Re-scored ${r.updated} transaction${r.updated === 1 ? "" : "s"} against your current earn rules.`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not recalculate");
    }
  }

  if (!meta) return <Spinner />;

  return (
    <>
      <PageHeader
        title="Transactions"
        subtitle={
          data
            ? `${data.total} transaction${data.total === 1 ? "" : "s"} · ${formatINR(data.totalAmount)} · ${formatPoints(data.totalPoints)} points`
            : undefined
        }
        actions={
          <>
            <button className="btn-secondary" onClick={() => setImportOpen(true)}>Import CSV</button>
            <button className="btn-secondary" onClick={() => void download("/data/export.csv", "transactions.csv")}>
              Export CSV
            </button>
            <button className="btn-secondary" onClick={recalculate} title="Re-run the earn rules over every stored transaction">
              Recalculate points
            </button>
            <BlockedAction blocked={activeCards.length === 0} reason="Add a card first">
            <button className="btn-primary" onClick={openNew} disabled={activeCards.length === 0}>
              Add transaction
            </button>
            </BlockedAction>
          </>
        }
      />

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {notice && (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {notice}
        </div>
      )}

      <Panel className="mb-6">
        <div className="grid gap-3 sm:grid-cols-4">
          <div>
            <label className="label" htmlFor="f-card">Card</label>
            <select id="f-card" className="input" value={filterCard} onChange={(e) => setFilterCard(e.target.value)}>
              <option value="">All cards</option>
              {cards.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="f-cat">Category</label>
            <select id="f-cat" className="input" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
              <option value="">All categories</option>
              {meta.categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="f-search">Merchant</label>
            <input
              id="f-search"
              className="input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search merchants…"
            />
          </div>
          <div>
            <label className="label" htmlFor="f-size">Rows per page</label>
            <select id="f-size" className="input" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
              {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
      </Panel>

      {!data ? (
        <Spinner />
      ) : data.transactions.length === 0 ? (
        <EmptyState
          title="No transactions found"
          hint={
            activeCards.length === 0
              ? "Add a card first, then log the spends you put on it."
              : "Adjust the filters, or log your first spend."
          }
          action={
            activeCards.length === 0 ? (
              <Link to="/cards" className="btn-primary">Add a card</Link>
            ) : (
              <button className="btn-primary" onClick={openNew}>Add transaction</button>
            )
          }
        />
      ) : (
        <Panel className="overflow-hidden" >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-ink-200">
                  <th className="th">Date</th>
                  <th className="th">Merchant</th>
                  <th className="th">Card</th>
                  <th className="th">Category</th>
                  <th className="th text-right">Amount</th>
                  <th className="th text-right">Points</th>
                  <th className="th"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {data.transactions.map((t) => (
                  <tr key={t.id} className="hover:bg-ink-50/60">
                    <td className="td whitespace-nowrap text-ink-500">{formatDate(t.date)}</td>
                    <td className="td">
                      <span className="font-medium text-ink-900">{t.merchant}</span>
                      {t.bookedVia && <span className="text-xs text-ink-500"> via {t.bookedVia}</span>}
                      {t.isInternational && <Badge tone="info">Intl</Badge>}
                      {/* Logged from a slip or an email alert and not yet seen on a statement. */}
                      {t.verification === "PENDING" && <Badge tone="warning">pending</Badge>}
                      {t.notes && (
                        <span className="block max-w-[14rem] truncate text-xs text-ink-500" title={t.notes}>
                          {t.notes}
                        </span>
                      )}
                    </td>
                    <td className="td">
                      <span className="inline-flex items-center gap-2 whitespace-nowrap">
                        <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: t.card?.color }} aria-hidden />
                        {t.card?.name}
                      </span>
                    </td>
                    <td className="td whitespace-nowrap text-ink-600">
                      {meta.categories.find((c) => c.key === t.category)?.label ?? t.category}
                      <span className="block text-xs text-ink-400">{t.channel === "ONLINE" ? "Online" : "In store"}</span>
                    </td>
                    <td className="td tnum whitespace-nowrap text-right font-medium">{formatINR(t.amount)}</td>
                    <td className="td tnum whitespace-nowrap text-right">
                      {formatPoints(t.pointsAwarded)}
                      {t.pointsOverride != null ? (
                        <span className="block text-xs text-amber-700">
                          manual · engine said {formatPoints(t.pointsEarned)}
                        </span>
                      ) : (
                        // Exclusion rules have long names; truncate so they
                        // cannot push the points column off the screen.
                        <>
                          {t.appliedRule && (
                            <span
                              className="ml-auto block max-w-[11rem] truncate text-xs text-ink-400"
                              title={t.appliedRule}
                            >
                              {t.appliedRule}
                            </span>
                          )}
                          {(t.bonusPoints ?? 0) > 0 && (
                            <span className="block text-xs text-ink-500">
                              {formatPoints(t.basePoints ?? 0)} + {formatPoints(t.bonusPoints ?? 0)} bonus
                              {t.bonusEarlyDate && t.bonusEarlyPoints ? (
                                <span className="block">
                                  {formatPoints(t.bonusEarlyPoints)} by {formatDate(t.bonusEarlyDate)}, the rest by{" "}
                                  {t.bonusCreditDate ? formatDate(t.bonusCreditDate) : "—"}
                                </span>
                              ) : (
                                t.bonusCreditDate && (
                                  <span className="block">
                                    bonus {new Date(t.bonusCreditDate) > new Date() ? "expected" : "credited"} {formatDate(t.bonusCreditDate)}
                                  </span>
                                )
                              )}
                            </span>
                          )}
                        </>
                      )}
                    </td>
                    <td className="td whitespace-nowrap text-right">
                      <button className="text-xs text-accent-700 underline" onClick={() => openEdit(t)}>Edit</button>
                      <button className="ml-3 text-xs text-red-700 underline" onClick={() => remove(t)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pager
            page={page}
            pageSize={pageSize}
            total={data.total}
            shown={data.transactions.length}
            onPage={setPage}
          />
        </Panel>
      )}

      <Modal open={formOpen} onClose={() => setFormOpen(false)} title={editing ? "Edit transaction" : "Add transaction"}>
        <form onSubmit={save} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label" htmlFor="t-card">Card</label>
              <select
                id="t-card"
                className="input"
                required
                value={form.cardId}
                onChange={(e) => setForm({ ...form, cardId: e.target.value })}
              >
                <option value="" disabled>Select a card</option>
                {cards.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label" htmlFor="t-date">Date</label>
              <input
                id="t-date"
                className="input"
                type="date"
                required
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              />
            </div>
            <div>
              <label className="label" htmlFor="t-amount">Amount (₹)</label>
              <input
                id="t-amount"
                className="input tnum"
                type="number"
                min="0.01"
                step="0.01"
                required
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
              />
            </div>
            <div>
              <label className="label" htmlFor="t-merchant">Merchant</label>
              <input
                id="t-merchant"
                className="input"
                required
                value={form.merchant}
                onChange={(e) => setForm({ ...form, merchant: e.target.value })}
                placeholder="e.g. Swiggy"
              />
            </div>
            <div>
              <label className="label" htmlFor="t-via">Booked through (optional)</label>
              <input
                id="t-via"
                className="input"
                list="t-via-portals"
                value={form.bookedVia}
                maxLength={60}
                onChange={(e) => setForm({ ...form, bookedVia: e.target.value })}
                placeholder="e.g. SmartBuy"
              />
              <datalist id="t-via-portals">
                {portalsFor(cards.find((c) => c.id === form.cardId)).map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
              <p className="mt-1 text-xs text-ink-500">
                The portal it was booked on, when the statement shows only the airline or hotel.
              </p>
            </div>
            <div>
              <label className="label" htmlFor="t-category">Category</label>
              <select
                id="t-category"
                className="input"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label" htmlFor="t-channel">Channel</label>
              <select
                id="t-channel"
                className="input"
                value={form.channel}
                onChange={(e) => setForm({ ...form, channel: e.target.value as "ONLINE" | "OFFLINE" })}
              >
                <option value="OFFLINE">In store / swipe</option>
                <option value="ONLINE">Online</option>
              </select>
            </div>
          </div>

          <div>
            <label className="label" htmlFor="t-notes">Notes (optional)</label>
            <input
              id="t-notes"
              className="input"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>

          <div className="flex flex-wrap gap-5">
            <label className="flex items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
                checked={form.isInternational}
                onChange={(e) => setForm({ ...form, isInternational: e.target.checked })}
              />
              International
            </label>
            <label className="flex items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
                checked={form.countsForMilestone}
                onChange={(e) => setForm({ ...form, countsForMilestone: e.target.checked })}
              />
              Counts toward milestones
            </label>
          </div>

          <div>
            <label className="label" htmlFor="t-override">Override points earned (optional)</label>
            <input
              id="t-override"
              className="input tnum"
              type="number"
              min={0}
              step="0.01"
              value={form.pointsOverride}
              onChange={(e) => setForm({ ...form, pointsOverride: e.target.value })}
              placeholder={preview ? `Engine says ${formatPoints(preview.points)} — leave blank to use it` : "Leave blank to use the earn rules"}
            />
            <p className="mt-1 text-xs text-ink-500">
              For bonus campaigns or corrections the rules can't express. An override sticks through
              recalculation; clear the field to hand the row back to the engine.
            </p>
          </div>

          {preview && (
            <div className="rounded-lg border border-ink-200 bg-ink-50 p-3 text-sm">
              <p className="font-medium text-ink-900">
                Earns {formatPoints(preview.points)} points
                {preview.excluded && " — this category earns nothing on this card"}
                {preview.capped && " (the accelerated cap is exhausted)"}
              </p>
              {preview.breakdown.map((leg, i) => (
                <p key={i} className="tnum mt-1 text-xs text-ink-600">
                  {formatINR(leg.spend)} at {leg.rateLabel} ({leg.ruleName}) → {formatPoints(leg.points)} pts
                </p>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setFormOpen(false)}>Cancel</button>
            <button className="btn-primary" disabled={saving}>{saving ? "Saving…" : "Save"}</button>
          </div>
        </form>
      </Modal>

      <ImportModal
        open={importOpen}
        cards={cards}
        categories={meta.categories}
        onClose={() => setImportOpen(false)}
        onDone={(count) => {
          setImportOpen(false);
          setNotice(`Imported ${count} transaction${count === 1 ? "" : "s"}.`);
          void load();
        }}
      />
    </>
  );
}

/** Page controls. Hidden when everything already fits on one page. */
function Pager({
  page,
  pageSize,
  total,
  shown,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  shown: number;
  onPage: (page: number) => void;
}) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  if (total <= pageSize) return null;

  const from = page * pageSize + 1;
  const to = page * pageSize + shown;

  return (
    <nav className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-ink-200 pt-4" aria-label="Pagination">
      <p className="tnum text-xs text-ink-500">
        Showing {from.toLocaleString("en-IN")}–{to.toLocaleString("en-IN")} of {total.toLocaleString("en-IN")}
      </p>
      <div className="flex items-center gap-2">
        <button className="btn-secondary px-3 py-1.5 text-xs" onClick={() => onPage(0)} disabled={page === 0}>
          First
        </button>
        <button
          className="btn-secondary px-3 py-1.5 text-xs"
          onClick={() => onPage(Math.max(0, page - 1))}
          disabled={page === 0}
        >
          Previous
        </button>
        <span className="tnum px-2 text-xs text-ink-600">
          Page {page + 1} of {pageCount}
        </span>
        <button
          className="btn-secondary px-3 py-1.5 text-xs"
          onClick={() => onPage(Math.min(pageCount - 1, page + 1))}
          disabled={page >= pageCount - 1}
        >
          Next
        </button>
        <button
          className="btn-secondary px-3 py-1.5 text-xs"
          onClick={() => onPage(pageCount - 1)}
          disabled={page >= pageCount - 1}
        >
          Last
        </button>
      </div>
    </nav>
  );
}

/* --------------------------------------------------------------- CSV import */

function ImportModal({
  open,
  cards,
  categories,
  onClose,
  onDone,
}: {
  open: boolean;
  cards: Card[];
  categories: { key: string; label: string }[];
  onClose: () => void;
  onDone: (count: number) => void;
}) {
  const [text, setText] = useState("");
  const [cardId, setCardId] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (open && !cardId) setCardId(cards[0]?.id ?? "");
  }, [open, cards, cardId]);

  async function run() {
    setProblem(null);
    const lines = text.trim().split(/\r?\n/).filter((l) => l.trim() !== "");
    if (lines.length === 0) {
      setProblem("Paste at least one row");
      return;
    }
    // Skip a header row if the first line doesn't start with a date.
    const rows = /^\d{4}-\d{2}-\d{2}/.test(lines[0]) ? lines : lines.slice(1);
    const valid = new Set(categories.map((c) => c.key));

    const parsed: Record<string, unknown>[] = [];
    for (const [i, line] of rows.entries()) {
      const cells = splitCsv(line);
      if (cells.length < 3) {
        setProblem(`Row ${i + 1}: expected at least date, merchant and amount`);
        return;
      }
      const [date, merchant, amount, category, channel] = cells;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date.trim())) {
        setProblem(`Row ${i + 1}: date must be YYYY-MM-DD`);
        return;
      }
      const value = Number(amount.replace(/[₹,\s]/g, ""));
      if (!Number.isFinite(value) || value <= 0) {
        setProblem(`Row ${i + 1}: "${amount}" is not a valid amount`);
        return;
      }
      const cat = (category ?? "").trim().toUpperCase();
      parsed.push({
        cardId,
        date: date.trim(),
        merchant: merchant.trim() || "Unknown",
        amount: value,
        category: valid.has(cat) ? cat : "OTHER",
        channel: (channel ?? "").trim().toUpperCase() === "ONLINE" ? "ONLINE" : "OFFLINE",
        isInternational: false,
        countsForMilestone: true,
      });
    }

    setBusy(true);
    try {
      const r = await api.post<{ imported: number; errors: { row: number; message: string }[] }>(
        "/transactions/import",
        { rows: parsed },
      );
      if (r.errors.length > 0) {
        setProblem(`${r.imported} imported, ${r.errors.length} failed. First error — row ${r.errors[0].row}: ${r.errors[0].message}`);
        return;
      }
      setText("");
      onDone(r.imported);
    } catch (err) {
      setProblem(err instanceof ApiError ? err.message : "Import failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Import transactions from CSV" wide>
      <div className="space-y-4">
        <div>
          <label className="label" htmlFor="i-card">Import into card</label>
          <select id="i-card" className="input" value={cardId} onChange={(e) => setCardId(e.target.value)}>
            {cards.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div>
          <label className="label" htmlFor="i-text">
            Paste rows as <code className="rounded bg-ink-100 px-1">date,merchant,amount,category,channel</code>
          </label>
          <textarea
            id="i-text"
            className="input h-48 font-mono text-xs"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={"2026-09-01,Swiggy,842,DINING,ONLINE\n2026-09-03,BigBasket,2150,GROCERY,ONLINE\n2026-09-05,Indian Oil,3000,FUEL,OFFLINE"}
          />
          <p className="mt-1.5 text-xs text-ink-500">
            A header row is skipped automatically. Category and channel are optional — unknown categories become “Other”.
            Points are computed on import using that card's earn rules.
          </p>
        </div>

        {problem && <ErrorBanner message={problem} onDismiss={() => setProblem(null)} />}

        <div className="flex justify-end gap-2">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={run} disabled={busy || !cardId}>
            {busy ? "Importing…" : "Import"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** Minimal CSV splitter that respects double-quoted fields. */
function splitCsv(line: string): string[] {
  const out: string[] = [];
  let current = "";
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"') {
        if (line[i + 1] === '"') { current += '"'; i++; }
        else quoted = false;
      } else current += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ",") { out.push(current); current = ""; }
    else current += ch;
  }
  out.push(current);
  return out;
}

/**
 * Portals a card's rules reward, to suggest for "booked through": the single
 * words its merchant keywords name, plus the portals issuers commonly use.
 */
function portalsFor(card: Card | undefined): string[] {
  const common = ["SmartBuy", "Travel EDGE", "Kotak Unbox", "Travel with Points", "PayZapp", "MyBiz"];
  const own = (card?.earnRules ?? [])
    .filter((r) => !r.isExclusion)
    .flatMap((r) => r.merchantPatterns)
    .filter((p) => p.trim() && !p.includes("+"));
  const seen = new Set<string>();
  return [...common, ...own].filter((p) => {
    const key = p.toLowerCase().replace(/\s+/g, "");
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
